import type { ReactNode } from "react";
import {
  Building2,
  Clock,
  FileText,
  LayoutDashboard,
  Receipt,
  Settings,
  StickyNote,
  UserRound,
  Users,
  Workflow,
} from "lucide-react";
import type { EntityHeaderPatternProps } from "../../patterns/EntityHeaderPattern";
import type { OperationBoardPatternProps } from "../../patterns/OperationBoardPattern";

export type EntitySectionItem = {
  id: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
};

export type EntityListItem = {
  id: string;
  title: string;
  subtitle?: string;
  info?: string;
  icon: ReactNode;
  status?: { label: string; tone: "neutral" | "success" | "warning" | "danger" };
  groupHeader?: boolean;
  groupColor?: string;
};

export type EntityWorkspaceBoardEntity = {
  id: string;
  header: EntityHeaderPatternProps;
  board: OperationBoardPatternProps;
};

export type EntityWorkspaceBoardConfig = {
  entityType: "client" | "ambassador" | "pilot";
  topbarTitle: string;
  listTitle: string;
  listIcon: ReactNode;
  headerSlot?: ReactNode;
  entities: EntityWorkspaceBoardEntity[];
  sections: EntitySectionItem[];
  entityListItems: EntityListItem[];
  boardSectionId: string;
  storageKey: string;
  defaultCollapsed?: boolean;
  defaultPinned?: boolean;
  allowHoverOpen?: boolean;
  defaultCollapsedGroups?: boolean;
  /** Override search input placeholder (e.g. "Rechercher par nom ou code…" for catalog). */
  searchPlaceholder?: string;
};

export const clientBoardConfig: EntityWorkspaceBoardConfig = {
  entityType: "client",
  topbarTitle: "Client",
  listTitle: "Clients",
  listIcon: <Users size={16} strokeWidth={1.5} />,
  storageKey: "xlflow.client.list.sidebar",
  allowHoverOpen: true,
  boardSectionId: "submissions",
  sections: [
    { id: "overview", label: "Vue d’ensemble", icon: <LayoutDashboard size={16} strokeWidth={1.5} /> },
    { id: "contacts", label: "Contacts", icon: <UserRound size={16} strokeWidth={1.5} /> },
    { id: "submissions", label: "Soumissions", icon: <FileText size={16} strokeWidth={1.5} />, active: true },
    { id: "invoices", label: "Factures", icon: <Receipt size={16} strokeWidth={1.5} /> },
    { id: "operations", label: "Opérations", icon: <Workflow size={16} strokeWidth={1.5} /> },
    { id: "history", label: "Historique", icon: <Clock size={16} strokeWidth={1.5} /> },
    { id: "notes", label: "Notes", icon: <StickyNote size={16} strokeWidth={1.5} /> },
  ],
  entityListItems: [
    {
      id: "c1",
      title: "Ferme des Trois Rivières",
      subtitle: "Nord",
      info: "3 contacts",
      icon: <Building2 size={16} strokeWidth={1.5} />,
      status: { label: "Actif", tone: "success" },
    },
    {
      id: "c2",
      title: "Agri-Plus",
      subtitle: "Est",
      info: "1 contact",
      icon: <Building2 size={16} strokeWidth={1.5} />,
      status: { label: "En attente", tone: "warning" },
    },
    {
      id: "c3",
      title: "Coopérative du Lac",
      subtitle: "Ouest",
      info: "0 contact",
      icon: <Building2 size={16} strokeWidth={1.5} />,
      status: { label: "Inactif", tone: "neutral" },
    },
  ],
  entities: [
    {
      id: "c1",
      header: {
        title: "Ferme des Trois Rivières",
        metaItems: [
          { label: "Statut", value: "Actif", tone: "default" },
          { label: "Responsable", value: "L. Tremblay", tone: "default" },
          { label: "Région", value: "Nord", tone: "muted" },
        ],
        status: { label: "Actif", tone: "success" },
        primaryAction: { label: "Nouvelle soumission" },
        secondaryActions: [{ label: "Rappel" }, { label: "Archiver" }],
      },
      board: {
        title: "Soumissions",
        stickyHeader: true,
        items: [
          {
            id: "s1",
            title: "Soumission #1042",
            subtitle: "Ferme du Nord",
            status: { label: "Actif", tone: "success" },
            amount: "$950",
            date: "2026-02-08",
            meta: "Échéance 14 jours",
          },
          {
            id: "s2",
            title: "Soumission #1043",
            subtitle: "Agri-Plus",
            status: { label: "En attente", tone: "warning" },
            amount: "$1,200",
            date: "2026-02-06",
            meta: "En validation",
          },
        ],
        rowActions: [{ label: "Voir" }, { label: "Archiver" }],
      },
    },
    {
      id: "c2",
      header: {
        title: "Agri-Plus",
        metaItems: [
          { label: "Statut", value: "En attente", tone: "default" },
          { label: "Responsable", value: "A. Gagnon", tone: "default" },
          { label: "Région", value: "Est", tone: "muted" },
        ],
        status: { label: "En attente", tone: "warning" },
        primaryAction: { label: "Nouvelle soumission" },
        secondaryActions: [{ label: "Rappel" }, { label: "Archiver" }],
      },
      board: {
        title: "Soumissions",
        stickyHeader: true,
        items: [
          {
            id: "s3",
            title: "Soumission #1102",
            subtitle: "Agri-Plus",
            status: { label: "En attente", tone: "warning" },
            amount: "$1,780",
            date: "2026-02-03",
            meta: "En validation",
          },
        ],
        rowActions: [{ label: "Voir" }, { label: "Archiver" }],
      },
    },
  ],
};

export const ambassadorBoardConfig: EntityWorkspaceBoardConfig = {
  entityType: "ambassador",
  topbarTitle: "Ambassadeur",
  listTitle: "Ambassadeurs",
  listIcon: <Users size={16} strokeWidth={1.5} />,
  storageKey: "xlflow.ambassador.list.sidebar",
  allowHoverOpen: true,
  boardSectionId: "submissions",
  sections: [
    { id: "overview", label: "Vue d'ensemble", icon: <LayoutDashboard size={16} strokeWidth={1.5} /> },
    { id: "clients", label: "Clients", icon: <Users size={16} strokeWidth={1.5} /> },
    { id: "submissions", label: "Soumissions", icon: <FileText size={16} strokeWidth={1.5} />, active: true },
    { id: "invoices", label: "Factures", icon: <Receipt size={16} strokeWidth={1.5} /> },
    { id: "deliveries", label: "Livraisons", icon: <Workflow size={16} strokeWidth={1.5} /> },
    { id: "success", label: "Suivi", icon: <Clock size={16} strokeWidth={1.5} /> },
    { id: "notes", label: "Notes", icon: <StickyNote size={16} strokeWidth={1.5} /> },
    { id: "settings", label: "Paramètres", icon: <Settings size={16} strokeWidth={1.5} /> },
  ],
  entityListItems: [
    {
      id: "a1",
      title: "Marie Gagnon",
      subtitle: "Est",
      info: "24 clients",
      icon: <UserRound size={16} strokeWidth={1.5} />,
      status: { label: "Actif", tone: "success" },
    },
    {
      id: "a2",
      title: "Sophie Tremblay",
      subtitle: "Nord",
      info: "18 clients",
      icon: <UserRound size={16} strokeWidth={1.5} />,
      status: { label: "Actif", tone: "success" },
    },
    {
      id: "a3",
      title: "Louis Gagné",
      subtitle: "Ouest",
      info: "6 clients",
      icon: <UserRound size={16} strokeWidth={1.5} />,
      status: { label: "Suspendu", tone: "danger" },
    },
  ],
  entities: [
    {
      id: "a1",
      header: {
        title: "Marie Gagnon",
        metaItems: [
          { label: "Statut", value: "Actif", tone: "default" },
          { label: "Région", value: "Est", tone: "muted" },
          { label: "Clients", value: "24", tone: "default" },
        ],
        status: { label: "Actif", tone: "success" },
        primaryAction: { label: "Nouvelle mission" },
        secondaryActions: [{ label: "Voir dossier" }, { label: "Suspendre" }],
      },
      board: {
        title: "Soumissions",
        stickyHeader: true,
        items: [
          {
            id: "m1",
            title: "Mission #221",
            subtitle: "Ferme du Nord",
            status: { label: "Actif", tone: "success" },
            amount: "$2,480",
            date: "2026-02-05",
            meta: "Planifiée",
          },
          {
            id: "m2",
            title: "Mission #222",
            subtitle: "Agri-Plus",
            status: { label: "En attente", tone: "warning" },
            amount: "$1,150",
            date: "2026-02-03",
            meta: "En validation",
          },
        ],
        rowActions: [{ label: "Voir" }, { label: "Archiver" }],
      },
    },
  ],
};

export const pilotBoardConfig: EntityWorkspaceBoardConfig = {
  entityType: "pilot",
  topbarTitle: "Pilote",
  listTitle: "Pilotes",
  listIcon: <Users size={16} strokeWidth={1.5} />,
  storageKey: "xlflow.pilot.list.sidebar",
  allowHoverOpen: true,
  boardSectionId: "submissions",
  sections: [
    { id: "overview", label: "Vue d'ensemble", icon: <LayoutDashboard size={16} strokeWidth={1.5} /> },
    { id: "submissions", label: "Soumissions", icon: <FileText size={16} strokeWidth={1.5} />, active: true },
    { id: "invoices", label: "Factures", icon: <Receipt size={16} strokeWidth={1.5} /> },
    { id: "deliveries", label: "Livraisons", icon: <Workflow size={16} strokeWidth={1.5} /> },
    { id: "success", label: "Suivi", icon: <Clock size={16} strokeWidth={1.5} /> },
    { id: "notes", label: "Notes", icon: <StickyNote size={16} strokeWidth={1.5} /> },
    { id: "certs", label: "Certifications", icon: <StickyNote size={16} strokeWidth={1.5} /> },
  ],
  entityListItems: [
    {
      id: "p1",
      title: "Jean Dupont",
      subtitle: "Sud",
      info: "3 certifications",
      icon: <UserRound size={16} strokeWidth={1.5} />,
      status: { label: "Actif", tone: "success" },
    },
    {
      id: "p2",
      title: "Marc Lavoie",
      subtitle: "Nord",
      info: "2 certifications",
      icon: <UserRound size={16} strokeWidth={1.5} />,
      status: { label: "Actif", tone: "success" },
    },
  ],
  entities: [
    {
      id: "p1",
      header: {
        title: "Jean Dupont",
        metaItems: [
          { label: "Statut", value: "Actif", tone: "default" },
          { label: "Certifications", value: "3", tone: "default" },
          { label: "Région", value: "Sud", tone: "muted" },
        ],
        status: { label: "Actif", tone: "success" },
        primaryAction: { label: "Assigner" },
        secondaryActions: [{ label: "Historique" }, { label: "Suspendre" }],
      },
      board: {
        title: "Soumissions",
        stickyHeader: true,
        items: [
          {
            id: "p1-1",
            title: "Mission #901",
            subtitle: "Ferme du Nord",
            status: { label: "Actif", tone: "success" },
            amount: "$820",
            date: "2026-02-07",
            meta: "En cours",
          },
          {
            id: "p1-2",
            title: "Mission #902",
            subtitle: "Coopérative du Lac",
            status: { label: "En retard", tone: "danger" },
            amount: "$1,620",
            date: "2026-01-29",
            meta: "Retard 7 jours",
          },
        ],
        rowActions: [{ label: "Voir" }, { label: "Archiver" }],
      },
    },
  ],
};
